// @flow
import React, { useState } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { SortableContainer } from "react-sortable-hoc";
import makeStyles from '@material-ui/core/styles/makeStyles';
import List from '@material-ui/core/List';
import Button from '@material-ui/core/Button';
import ArrayItem from './ArrayItem';
import DefaultItemComponent from './DefaultItemComponent';

const arrayMove = (array, from, to) => {
    const _array = array.slice();
    _array.splice(to < 0 ? _array.length + to : to, 0, _array.splice(from, 1)[0]);
    return _array;
};

const useStyles = makeStyles({
    editButton: {
        textTransform: 'none'
    },
    sortableHelper: {
        zIndex: 9999
    }
});

const SortableListContainer = SortableContainer(List);

type Props = $ReadOnly<{
    label: Node,
    path: $ReadOnlyArray<string>,
    value: $ReadOnlyArray<any>,
    expanded?: boolean,
    canClose?: boolean,
    editable?: boolean,
    sortable?: boolean,
    minItems?: number,
    maxItems?: number,
    ItemComponent?: Node,
    inputComponent?: Node,
    parseLabel?: (value: any) => Node,
    append?: Node,

    onChange: (path: $ReadOnlyArray<string>, value: $ReadOnlyArray<any>) => void
}>;

function ArrayComponent({ label, path, value, expanded, canClose = true, editable, sortable, minItems, maxItems, ItemComponent = DefaultItemComponent, inputComponent, parseLabel, append, onChange }: Props) {
    const classes = useStyles();
    const [isExpanded, setIsExpanded] = useState(expanded);

    const expand = () => setIsExpanded(true);
    const collapse = () => setIsExpanded(false);

    const canAdd = !maxItems || !value || value.length < maxItems;
    const canRemove = !minItems || (value && value.length > minItems);

    const addItem = (newItem) => {
        if(canAdd) {
            onChange(path, [...(value || []), newItem]);
        }
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        onChange(path, arrayMove(value, oldIndex, newIndex));
    };

    const deleteItem = (index) => {
        if(canRemove) {
            const newValue = [].concat(value);
            newValue.splice(index, 1);

            onChange(path, newValue);
        }
    };

    const handleItemChange = (newValue, index) => {
        const newItems = [].concat(value);
        newItems.splice(index, 1, newValue);
        onChange(path, newItems);
    };

    return (
        <div>
            <span>{label}:</span>
            {!isExpanded &&
                <span>
                    <FormattedMessage
                        id="config.array.itemsCount"
                        defaultMessage="{count} items"
                        values={{
                            count: value ? value.length : 0
                        }}
                    />
                    <Button className={classes.editButton} component="a" onClick={expand}>
                        <FormattedMessage
                            id="config.array.edit"
                            defaultMessage="Edit"
                        />
                    </Button>
                </span>
            }
            {isExpanded &&
                <div>
                    <SortableListContainer
                        helperClass={classes.sortableHelper}
                        component="div"
                        useDragHandle
                        onSortEnd={onSortEnd}
                    >
                        {value && value.map((item, index) => (
                            <ArrayItem
                                key={index}
                                index={index}
                                value={item}
                                canRemove={canRemove}
                                editable={editable}
                                sortable={sortable}
                                ItemComponent={ItemComponent}
                                inputComponent={inputComponent}
                                parseLabel={parseLabel}
                                append={append}
                                onChange={handleItemChange}
                                onDelete={deleteItem}
                            />
                        ))}
                    </SortableListContainer>

                    {canAdd &&
                        <ItemComponent
                            form
                            canAdd={canAdd}
                            editable
                            inputComponent={inputComponent}
                            onSubmit={addItem}
                        />
                    }

                    {(canClose || !expanded) &&
                        <Button onClick={collapse}>
                            <FormattedMessage
                                id="config.array.close"
                                defaultMessage="Close"
                            />
                        </Button>
                    }
                </div>
            }
        </div>
    );
}

export default ArrayComponent;